import { Component } from '@theme/component';

/**
 * @typedef {object} CustomEngravingOptionRefs
 * @property {HTMLInputElement} enableEngraving - Opt-in checkbox.
 * @property {HTMLDivElement} engravingFields - Container for the text field.
 * @property {HTMLInputElement} engravingText - Engraving text input.
 * @property {HTMLElement} characterCount - Character count display.
 * @property {HTMLElement} [errorMessage] - Validation error container.
 */

/**
 * @extends {Component<CustomEngravingOptionRefs>}
 */
class CustomEngravingOption extends Component {
  requiredRefs = ['enableEngraving', 'engravingFields', 'engravingText', 'characterCount'];

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {((event: Event) => void) | null} */
  #clickBound = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setEngravingEnabled(false);

    const formId = this.refs.engravingText.getAttribute('form');
    this.#productForm = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;

    this.#clickBound = this.#handleFormClick.bind(this);
    this.#productForm?.addEventListener('click', this.#clickBound, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#clickBound) {
      this.#productForm?.removeEventListener('click', this.#clickBound, true);
      this.#clickBound = null;
    }
    this.#productForm = null;
  }

  /**
   * @param {Event} _event
   */
  toggleEngraving(_event) {
    this.#setEngravingEnabled(this.refs.enableEngraving.checked);
  }

  handleInput() {
    this.#updateCharacterCount();
    this.refs.engravingText.setCustomValidity('');
    this.#hideError();
  }

  /**
   * @param {boolean} enabled
   */
  #setEngravingEnabled(enabled) {
    const { engravingFields, engravingText } = this.refs;

    engravingFields.hidden = !enabled;
    engravingText.disabled = !enabled;

    if (enabled) {
      engravingText.setAttribute('required', 'required');
      engravingText.setAttribute('aria-required', 'true');
      engravingText.focus();
    } else {
      engravingText.removeAttribute('required');
      engravingText.removeAttribute('aria-required');
      engravingText.removeAttribute('aria-invalid');
      engravingText.value = '';
      engravingText.setCustomValidity('');
      this.#hideError();
    }

    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    const { characterCount, engravingText } = this.refs;
    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const maxLength = engravingText.maxLength || Number(characterCount.getAttribute('data-max')) || 30;
    const updatedText = template
      .replace('[current]', engravingText.value.length.toString())
      .replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  /**
   * @param {Event} event
   */
  #handleFormClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.#productForm) return;

    const submitControl = target.closest('button[name="add"], button[type="submit"], input[type="submit"]');
    if (!submitControl || !this.#productForm.contains(submitControl)) return;

    if (!this.#validateEngraving()) {
      event.preventDefault();
      event.stopPropagation();
      this.#showError();
      this.refs.engravingText.focus();
    }
  }

  /**
   * @returns {boolean}
   */
  #validateEngraving() {
    const { enableEngraving, engravingText } = this.refs;

    if (!enableEngraving.checked) {
      engravingText.setCustomValidity('');
      return true;
    }

    const text = engravingText.value.trim();
    if (text.length === 0) {
      engravingText.setCustomValidity(
        Theme.translations?.custom_engraving_validation || 'Enter engraving text to continue.'
      );
      return false;
    }

    engravingText.value = text;
    engravingText.setCustomValidity('');

    if (!engravingText.checkValidity()) {
      return false;
    }

    this.#hideError();
    return true;
  }

  #showError() {
    const { errorMessage, engravingText } = this.refs;
    if (!errorMessage) return;

    errorMessage.classList.remove('hidden');
    engravingText.setAttribute('aria-invalid', 'true');
    if (errorMessage.id) {
      engravingText.setAttribute('aria-describedby', errorMessage.id);
    }
  }

  #hideError() {
    const { errorMessage, engravingText } = this.refs;
    if (!errorMessage) return;

    errorMessage.classList.add('hidden');
    engravingText.removeAttribute('aria-invalid');
    engravingText.removeAttribute('aria-describedby');
  }
}

if (!customElements.get('custom-engraving-option')) {
  customElements.define('custom-engraving-option', CustomEngravingOption);
}
